import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ToastService } from "../../../shared/services/toast.service";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { PageChildren, PageModel } from "../../../shared/models/page.model";
import { ReportCategoryModel } from "../../../shared/models/report-category.model";
import { CategoryTypes } from "../../../shared/enums/categoryTypes.enum";
import { PagesManagementService } from "../../../shared/services/pages-management.service";
import { CategoriesService } from "../../../shared/services/categories.service";
import { firstValueFrom, forkJoin, Observable, Observer, of, Subscription } from "rxjs";
import {
  ConfirmDialogComponent,
  ConfirmDialogModel
} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ConfirmDialogAnswers } from "../../../shared/enums/confirmDialogAnswers.enum";
import { ToastMessageTypes } from "../../../shared/enums/toastMessageTypes.enum";
import { ContentSeas } from "../../../shared/enums/contentSeas.enum";

@Component({
  selector: 'app-add-page',
  templateUrl: './add-page.component.html',
  styleUrls: ['./add-page.component.scss']
})
export class AddPageComponent implements OnInit {

  public form: FormGroup;

  private hasUnsavedData = false;
  private formChangeSubscription: Subscription | undefined;

  public pages: PageModel[] | undefined;
  public categories: ReportCategoryModel[] | undefined

  public categoryTypes = CategoryTypes;
  public areaSeas = ContentSeas;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private pagesService: PagesManagementService,
    private categoriesService: CategoriesService,
    private fb: FormBuilder,
    private toast: ToastService,
    private translate: TranslateService,
    public dialogRef: MatDialogRef<AddPageComponent>,
    private dialog: MatDialog
  ) {
    this.form = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3)]],
      subTitle: '',
      main: false,
      categoryType: CategoryTypes.NONE,
      children: this.fb.array([])
    }, { validators: [
        this.checkedPageType('categoryType'),
        this.checkedSea('children'),
        this.notEmptyChildrenValidator('children')]
    });


    this.formChangeSubscription = this.form.valueChanges.subscribe((value) => {
      this.hasUnsavedData = true;
    });
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.hasUnsavedData) {
      return new Observable((observer: Observer<any>) => {

        const message = this.translate.instant('DIALOGS.CHANGES_ARE_NOT_SAVED');
        const dialogData = new ConfirmDialogModel(this.translate.instant('DIALOGS.CHANGES_ARE_NOT_SAVED_TITLE'), message);

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          maxWidth: "400px",
          data: dialogData,
          restoreFocus: false
        });

        dialogRef.afterClosed().subscribe(dialogResult => {
          if (dialogResult === ConfirmDialogAnswers.YES) {
            observer.next(true);
          } else if (dialogResult === ConfirmDialogAnswers.NO) {
            observer.next(false);
          } else if (dialogResult === ConfirmDialogAnswers.SAVE_AND_EXIT) {
            this.createPage(false).subscribe({
              next:value => {
                observer.next(value);
              }, error: err => {
                observer.next(false);
              }
            })
          } else {
            observer.next(false);
          }
        });
      });
    } else {
      return of(true);
    }
  }

  ngOnInit(): void {
    this.getData();
  }

  checkedSea(controlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];

      // need to have sea selected
      let seaSelected = true;
      control.value.forEach((value:any) => {
        if (value.sea !== ContentSeas.RED && value.sea !== ContentSeas.MEDITERRANEAN) {
          seaSelected = false
        }
      })

      // set error if validation fails
      if (control.value.length > 0 && !seaSelected) {
        control.setErrors({ seaRequired: true });
      }

    }
  }

  checkedPageType(controlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];

      // set error if validation fails
      if (!(!this.form?.get('main')?.value ||
        this.form?.get('main')?.value && (control.value === CategoryTypes.VIOLATION ||
          control.value === CategoryTypes.OBSERVATION))) {
        control.setErrors({ categoryRequired: true });
      }
    }
  }

  notEmptyChildrenValidator(controlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];

      // need to have at least one category selected
      let categorySelected = false;
      control.value.forEach((value:PageChildren) => {
        if (value.categoryId > 0) categorySelected = true
      })

      // set error if validation fails
      if (control.value.length > 0 && !categorySelected) {
        control.setErrors({ emptyChildren: true });
      }
    }
  }

  addNewChild() {
    const max_order = Math.max(...this.form.value.children.map((child:PageChildren) => child.order), 0) + 1;
    this.pageChildren.push(this.newChild(0, 0, max_order, ContentSeas.NONE, false));
  }

  removeChild(i:number) {
    this.pageChildren.removeAt(i);
  }

  get pageChildren() {
    return this.form.get('children') as FormArray;
  }

  gotoChild(pageId: number) {
    // this.breadcrumbsService.addBreadcrumb(pageId);
    this.router.navigate([`/page-view/${pageId}`], {replaceUrl: true});
  }

  newChild(categoryId: number, pageId: number, order: number, sea: ContentSeas, promoted: boolean): FormGroup {
    return this.fb.group({
      categoryId,
      pageId,
      order,
      sea,
      promoted
    })
  }

  public checkError = (controlName: string, errorName: string) => {
    return this.form.controls[controlName].hasError(errorName);
  }

  getData() {

    let requests = [
      this.pagesService.getPages(),
      this.categoriesService.getCategories()
    ];

    forkJoin(requests).subscribe({
      next: (responses: any) => {

        // responses[0] - all pages
        this.pages = responses[0];

        // responses[1] - all categories
        this.categories = responses[1];

        // mark form with updated data as untouched yet
        this.hasUnsavedData = false;

      },
      error: (error) => {}
    });

  }

  getPageTitle(id: number) {
    return this.pages?.find(page => page.id === id)?.title;
  }

  // ToDo: Remove this temporary solution
  choosePage(child: any, index: number) {
    let selectBox = document.getElementsByClassName('page-select-' + index) as HTMLCollectionOf<HTMLElement>;
    selectBox && selectBox[0].click();
  }

  checkCategoryType() {
    if (!this.form.get('main')?.value) this.form.get('categoryType')?.patchValue(CategoryTypes.NONE);
  }

  checkMain() {
    this.form.get('main')?.patchValue(true);
  }

  async startPageCreating() {
    await firstValueFrom(this.createPage());
  }

  createPage(defaultNavigation = true): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      if (this.form.valid) {

        let data = this.form.value;

        data.children = data.children.map((child: PageChildren) => {
          if (child.pageId && child.pageId > 0) return child;

          return {
            order: child.order,
            promoted: child.promoted,
            categoryId: child.categoryId,
            sea: child.sea
          };
        });

        this.pagesService.addPage(data).subscribe({
          next: (resp: any) => {
            this.toast.show(this.translate.instant('SUCCESS_MESSAGES.UPDATED_PAGE'));
            this.hasUnsavedData = false;
            if (defaultNavigation) this.router.navigate(['/pages-management/']);

            observer.next(true);
          },
          error: (error) => {
            observer.next(false);
          },
        });
      } else {
        this.toast.show(this.translate.instant('ERROR_MESSAGES.CANT_SAVE_THIS_DATA_YET'), ToastMessageTypes.ERROR);
        observer.next(false);
      }
    })
  }

  ngOnDestroy() {
    this.formChangeSubscription?.unsubscribe();
  }

}
